/**
 * Coverage skeleton registry and merge utilities
 */

import { logger } from "@platform/browser-logger";
import { CoverageTypeEnum as V5CoverageTypeEnum } from "@platform/openapi-types";
import { CoverageTypeEnum } from "@platform/types";

import { getBasicLifeSkeleton } from "../../locs/basic-life";
import { getDentalPpoSkeleton } from "../../locs/dental-ppo";
import { LOC_MAP, type LocCoverageType } from "../../locs/locMap";
import { getStdSkeleton } from "../../locs/std";
import { getVisionSkeleton } from "../../locs/vision";
import { RfpBenefitPlan } from "../../types/rfp.api.types";
import { ApiCommission } from "../commissionHelpers";
import { injectCommissionsIntoCoverage } from "../commissionInjection";
import { flattenRateGuaranteeIntoBenefits } from "../rateGuaranteeFields";
import {
  RfpCoverage,
  RfpCoverageResponse,
  RfpData,
  RfpResponse,
} from "../types";

import {
  createEmptyBenefitPlanSkeleton,
  createEmptyCoverageSkeleton,
  createEmptyRfpSkeleton,
  deepMerge,
} from "./base";
import { getCriticalIllnessSkeleton } from "./criticalIllness";
import { getDentalSkeleton } from "./dental";
import { getLtdSkeleton } from "./ltd";

export {
  createEmptyBenefitPlanSkeleton,
  createEmptyCoverageSkeleton,
  createEmptyRfpSkeleton,
  deepMerge,
};

// Export individual coverage skeletons for direct use if needed
// Note: Cancer, Hospital Indemnity, Vision, Basic Life, Voluntary Life, STD, LTD, Critical Illness, Accident, and DHMO are in LOC_MAP, not here
export {
  getBasicLifeSkeleton,
  getCriticalIllnessSkeleton,
  getDentalPpoSkeleton,
  getDentalSkeleton,
  getLtdSkeleton,
  getStdSkeleton,
  getVisionSkeleton,
};

/**
 * Registry mapping coverage types to their skeleton functions
 */
const coverageSkeletonRegistry: Record<
  CoverageTypeEnum,
  () => Partial<RfpCoverage>
> = {
  // ACCIDENT migrated to LOC_MAP
  [CoverageTypeEnum.ACCIDENT]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.ACCIDENT),
  // HOSPITAL_INDEMNITY migrated to LOC_MAP
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.HOSPITAL_INDEMNITY),
  [CoverageTypeEnum.BASIC_LIFE_ADND]: getBasicLifeSkeleton,
  // VOLUNTARY_LIFE_ADND migrated to LOC_MAP
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.VOLUNTARY_LIFE_ADND),
  [CoverageTypeEnum.CRITICAL_ILLNESS]: getCriticalIllnessSkeleton,
  // STD migrated to LOC_MAP
  [CoverageTypeEnum.STD]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.STD),
  [CoverageTypeEnum.LTD]: getLtdSkeleton,
  [CoverageTypeEnum.DENTAL]: getDentalSkeleton,
  [CoverageTypeEnum.DENTAL_PPO]: getDentalPpoSkeleton,
  [CoverageTypeEnum.DENTAL_HMO]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.DENTAL_HMO),
  [CoverageTypeEnum.VISION]: getVisionSkeleton,
  // Coverage types without specific skeletons yet - will use base skeleton
  // CANCER migrated to LOC_MAP
  [CoverageTypeEnum.CANCER]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.CANCER),
  [CoverageTypeEnum.FMLA]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.FMLA),
  // EAP migrated to LOC_MAP
  [CoverageTypeEnum.EAP]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.EAP),
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: () =>
    createEmptyCoverageSkeleton(
      CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE,
    ),
  [CoverageTypeEnum.WHOLE_LIFE]: () =>
    createEmptyCoverageSkeleton(CoverageTypeEnum.WHOLE_LIFE),
};

/**
 * Gets a skeleton for a specific coverage type with appropriate default structure
 *
 * @param type - The coverage type to create a skeleton for
 * @returns A fully structured skeleton for the coverage type
 */
export const getCoverageTypeSkeleton = (
  type: CoverageTypeEnum,
): Partial<RfpCoverage> => {
  const skeletonFn = coverageSkeletonRegistry[type];
  return skeletonFn ? skeletonFn() : createEmptyCoverageSkeleton(type);
};

/**
 * Merges a benefit plan from the backend with an empty skeleton
 * Ensures all expected fields exist, preferring backend data
 *
 * @param backendPlan - The benefit plan from backend
 * @param coverageType - Optional coverage type to use type-specific skeleton
 */
export const mergeBenefitPlanWithSkeleton = (
  backendPlan: Partial<RfpBenefitPlan>,
  coverageType?: LocCoverageType,
): RfpBenefitPlan => {
  let skeleton: Partial<RfpBenefitPlan>;

  // If we have a coverage type, try to get type-specific skeleton
  if (coverageType) {
    const coverageSkeleton = getCoverageTypeSkeleton(coverageType);
    // Use the first benefit plan from the coverage skeleton if available
    skeleton =
      (coverageSkeleton.benefitPlans?.[0] as Partial<RfpBenefitPlan>) ||
      createEmptyBenefitPlanSkeleton();
    logger.debug(`Using type-specific skeleton for: ${coverageType}`);
  } else {
    skeleton = createEmptyBenefitPlanSkeleton();
    logger.debug("Using generic skeleton");
  }

  // Apply coverage-specific transformations to backend data
  let transformedBackendPlan = backendPlan;

  if (!backendPlan.benefits) {
    // No benefits to transform
  } else if (coverageType) {
    const locConfig = LOC_MAP[coverageType];
    if (locConfig) {
      /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any */
      transformedBackendPlan = {
        ...backendPlan,
        benefits: locConfig.transformFromApi(
          backendPlan.benefits as any,
        ) as any,
      };
      /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any */
    }
  }

  const merged = deepMerge(skeleton, transformedBackendPlan) as RfpBenefitPlan;

  // Rate guarantee / rate cap persist at plan level but are edited as
  // benefit-body cells; the save path lifts them back out.
  merged.benefits = flattenRateGuaranteeIntoBenefits(
    merged.benefits,
    merged.rateGuarantee,
    merged.rateCap,
  );

  return merged;
};

/**
 * Merges a coverage from the backend with an empty skeleton
 * Ensures all expected fields exist, preferring backend data
 * Uses type-specific skeleton if available
 */
export const mergeCoverageWithSkeleton = (
  backendCoverage: Partial<RfpCoverage>,
): RfpCoverage => {
  // Use type-specific skeleton that has all the proper structure
  const skeleton = getCoverageTypeSkeleton(
    backendCoverage.type || CoverageTypeEnum.ACCIDENT,
  );

  // Merge the coverage itself
  const mergedCoverage = deepMerge(skeleton, backendCoverage);

  // If backend has benefit plans, merge each one with its skeleton
  if (backendCoverage.benefitPlans && backendCoverage.benefitPlans.length > 0) {
    logger.debug(`mergeCoverageWithSkeleton - merging backend benefit plans:`, {
      benefitPlans: JSON.stringify(backendCoverage.benefitPlans, null, 2),
    });
    logger.debug(
      `mergeCoverageWithSkeleton - backend benefit plan 0 benefits:`,
      {
        benefits: JSON.stringify(
          backendCoverage.benefitPlans[0]?.benefits,
          null,
          2,
        ),
      },
    );
    mergedCoverage.benefitPlans = backendCoverage.benefitPlans.map((plan) =>
      mergeBenefitPlanWithSkeleton(
        plan,
        backendCoverage.type as LocCoverageType,
      ),
    );
  } else if (skeleton.benefitPlans && skeleton.benefitPlans.length > 0) {
    // If backend has no plans but skeleton does, use skeleton's plans
    // This ensures we have at least the default plan structure
    logger.debug(`mergeCoverageWithSkeleton - using skeleton benefit plans:`, {
      benefitPlans: JSON.stringify(skeleton.benefitPlans, null, 2),
    });
    mergedCoverage.benefitPlans = skeleton.benefitPlans.map((plan) =>
      mergeBenefitPlanWithSkeleton(
        plan as RfpBenefitPlan,
        backendCoverage.type as LocCoverageType,
      ),
    );
  }

  return mergedCoverage as RfpCoverage;
};

// Transforms V5 CoverageTypeEnum to CoverageTypeEnum used in frontend which adds dental-hmo/ppo
export const transformCoverageType = (
  coverage: RfpCoverageResponse,
): CoverageTypeEnum => {
  switch (coverage.type) {
    case V5CoverageTypeEnum.dental: {
      const planType = coverage.benefitPlans?.[0]?.type?.toLowerCase();
      return planType === "hmo"
        ? CoverageTypeEnum.DENTAL_HMO
        : CoverageTypeEnum.DENTAL_PPO; // Default to PPO if not specified
    }
    case V5CoverageTypeEnum.accident:
      return CoverageTypeEnum.ACCIDENT;
    case V5CoverageTypeEnum.hospitalIndemnity:
      return CoverageTypeEnum.HOSPITAL_INDEMNITY;
    case V5CoverageTypeEnum.basicLifeAdnd:
      return CoverageTypeEnum.BASIC_LIFE_ADND;
    case V5CoverageTypeEnum.voluntaryLifeAdnd:
      return CoverageTypeEnum.VOLUNTARY_LIFE_ADND;
    case V5CoverageTypeEnum.criticalIllness:
      return CoverageTypeEnum.CRITICAL_ILLNESS;
    case V5CoverageTypeEnum.std:
      return CoverageTypeEnum.STD;
    case V5CoverageTypeEnum.ltd:
      return CoverageTypeEnum.LTD;
    case V5CoverageTypeEnum.cancer:
      return CoverageTypeEnum.CANCER;
    case V5CoverageTypeEnum.fmla:
      return CoverageTypeEnum.FMLA;
    case V5CoverageTypeEnum.eap:
      return CoverageTypeEnum.EAP;
    case V5CoverageTypeEnum.individualDisabilityInsurance:
      return CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE;
    case V5CoverageTypeEnum.wholeLife:
      return CoverageTypeEnum.WHOLE_LIFE;
    case V5CoverageTypeEnum.vision:
      return CoverageTypeEnum.VISION;
    default:
      logger.warn(
        `transformCoverageType - Unknown backend coverage type: ${coverage.type}`,
      );
      return coverage.type;
  }
};

/**
 * Extracts product type from coverage's benefit plans.
 *
 * Note: This assumes all benefit plans within a coverage have the same product type
 * If plans have different types, only the first plan's type is returned.
 *
 * @param coverage - Coverage response from backend
 * @returns Product type fomr BenefitPlanTypeEnum (e.g., "hmo", "ppo") or undefined if no plans exist
 */
const extractProductTypeFromCoverage = (
  coverage: RfpCoverageResponse,
): string | undefined => {
  if (!coverage.benefitPlans || coverage.benefitPlans.length === 0) {
    return undefined;
  }
  return coverage.benefitPlans[0].type;
};

/**
 * Transforms a single coverage from backend format to frontend format
 * - Backend sends type: "dental" (generic) with benefitPlans[0].type indicating "ppo" or "hmo"
 * - Frontend needs specific types: "dental-ppo" or "dental-hmo" for routing/configuration
 *
 * @param coverage - Coverage from backend in RfpCoverageResponse format
 * @returns Transformed coverage with frontend-specific type
 */
export const transformSingleCoverage = (
  coverage: RfpCoverageResponse,
): RfpCoverage => {
  const transformedType = transformCoverageType(coverage);
  return {
    ...coverage,
    type: transformedType,
    coverageType: coverage.type,
    productType: extractProductTypeFromCoverage(coverage),
  };
};

/**
 * Transforms coverages from backend format to frontend format
 *
 * @param coverages - Array of coverages from backend
 * @returns Transformed and sorted coverages
 */
export const transformRfpCoverages = (
  coverages: RfpCoverageResponse[],
): RfpCoverage[] =>
  // Only split dental into separate HMO/PPO coverages
  // All other coverage types should keep their full benefit plan arrays
  coverages
    .flatMap((c) => {
      // For dental, split into separate coverages by product type (HMO/PPO)
      if (c.type === "dental") {
        // Group benefit plans by their product type
        const hmoPlans = c.benefitPlans.filter(
          (bp) => bp.type?.toLowerCase() === "hmo",
        );
        const ppoPlans = c.benefitPlans.filter(
          (bp) => bp.type?.toLowerCase() === "ppo" || !bp.type,
        );

        const result: RfpCoverageResponse[] = [];
        if (hmoPlans.length > 0) {
          result.push({ ...c, benefitPlans: hmoPlans });
        }
        if (ppoPlans.length > 0) {
          result.push({ ...c, benefitPlans: ppoPlans });
        }
        return result;
      }

      // For all other coverage types, keep all benefit plans together
      return [c];
    })
    .map(transformSingleCoverage)
    .sort((a, b) => {
      // Sort coverages alphabetically by type for consistency
      if (a.type < b.type) return -1;
      if (a.type > b.type) return 1;
      return 0;
    });

/**
 * Merges an RFP from the backend with an empty skeleton
 * Ensures all expected fields exist, preferring backend data
 *
 * Also transforms backend-specific fields to frontend expectations:
 * - updatedAt → lastModified
 * - Missing name/notes get empty defaults
 */
export const mergeRfpWithSkeleton = (backendRfp: RfpResponse): RfpData => {
  const skeleton = createEmptyRfpSkeleton(backendRfp.id || "");

  // Map backend commission format (benefitPlanId) to ApiCommission format (benefitPlanIdentifier)
  const brokerCommissions: ApiCommission[] =
    backendRfp.broker?.commissions?.map((commission) => ({
      coverageType: commission.coverageType,
      benefitPlanIdentifier: commission.benefitPlanId,
      benefitPlanId: commission.benefitPlanId,
      currentType: commission.currentType,
      currentAmount: commission.currentAmount,
      currentHeapedAmount: commission.currentHeapedAmount,
      requestedType: commission.requestedType,
      requestedAmount: commission.requestedAmount,
      requestedHeapedAmount: commission.requestedHeapedAmount,
    })) ?? [];

  // Transform backend response to match RfpData structure
  const transformedRfp: Partial<RfpData> = {
    id: backendRfp.id || backendRfp.identifier,
    coverages: [],
    // TODO: do we need the following fields transformed?
    lastModified: backendRfp.updatedAt,
    name: "", // Backend doesn't provide name yet
    notes: "", // Backend doesn't provide notes yet
    broker:
      brokerCommissions.length > 0
        ? { commissions: brokerCommissions }
        : undefined,
  };

  // Transform dental coverage types from backend format
  // Backend sends type: "dental" (generic) with benefitPlans[0].type indicating "ppo" or "hmo"
  // Frontend needs specific types: "dental-ppo" or "dental-hmo" for routing/configuration
  if (backendRfp.coverages) {
    const transformedCoverages = transformRfpCoverages(backendRfp.coverages);
    transformedRfp.coverages = transformedCoverages;
  }

  // Merge with skeleton
  const mergedRfp = deepMerge(skeleton, transformedRfp);
  // Merge each coverage with its skeleton
  if (transformedRfp.coverages && transformedRfp.coverages.length > 0) {
    logger.debug(
      `mergeRfpWithSkeleton - merging coverages, count: ${transformedRfp.coverages.length}`,
    );
    mergedRfp.coverages = transformedRfp.coverages.map((coverage, index) => {
      logger.debug(
        `mergeRfpWithSkeleton - processing coverage ${index}: ${JSON.stringify(coverage, null, 2)}`,
      );
      const mergedCoverage = mergeCoverageWithSkeleton(coverage);

      // Inject commission data from broker.commissions into each benefit plan's benefits
      if (brokerCommissions.length > 0) {
        return injectCommissionsIntoCoverage(mergedCoverage, brokerCommissions);
      }
      return mergedCoverage;
    });
  } else {
    logger.debug("mergeRfpWithSkeleton - NO COVERAGES IN BACKEND RESPONSE!");
  }

  return mergedRfp;
};
